import { memo, useMemo } from 'react'
import { PlotTypesMenuItemType, SeriesOutput } from '../../chart.typings'
import { Grid, Typography } from '@mui/material'
import SeriesOutputPlotTypesView from './seriesOutputPlotTypes'
import { getChartTypeFromOutput } from '../../../../util/chart'
import {
    IndicatorOutput_OutputStyle,
    IndicatorOutput_OutputType,
    indicatorOutput_OutputTypeFromJSON
} from '../../../../proto/generated/indicator'
import { ChartSubType, ChartType } from '../../../../proto/generated/chart'
import Checkbox from '@mui/material/Checkbox'
import ColorSelector, { LineThickness } from '../../../colorSelector/colorSelector'
import { clampLineThickness } from '../../../colorSelector/lineStyle.types'
import tinycolor from 'tinycolor2'

const OI_PROFILE_COLOR_SETTINGS = [
    { key: 'primaryCallColor', label: 'Primary Call Color' },
    { key: 'secondaryCallColor', label: 'Secondary Call Color' },
    { key: 'primaryPutColor', label: 'Primary Put Color' },
    { key: 'secondaryPutColor', label: 'Secondary Put Color' },
] as const

const resolveOiProfileColorKey = (colorName: string): string | undefined => {
    const byKey = OI_PROFILE_COLOR_SETTINGS.find((s) => s.key === colorName)
    if (byKey) return byKey.key
    const byLabel = OI_PROFILE_COLOR_SETTINGS.find((s) => s.label === colorName)
    return byLabel?.key
}

const getOiProfileColorValue = (seriesOutput: SeriesOutput, key: string, label: string): string => {
    const fromSettings = seriesOutput.drawingSettings?.settings?.[key]
    if (fromSettings) return fromSettings
    return seriesOutput.colors?.find((c) => c.title === key || c.title === label)?.colorCode ?? '#2962ff'
}

export type SeriesOutputViewProps = {
    onChange: (seriesOutput: SeriesOutput) => void
    seriesOutput: SeriesOutput,
    hideTitle: boolean,
}
export const SeriesOutputView = ({ onChange, seriesOutput, hideTitle = false }: SeriesOutputViewProps) => {
  
    const handleVisibilityChange = (visibility: boolean) => {
        const newOutput = {
            ...seriesOutput,
            visibility: visibility
        };
       onChange(newOutput);

    };
    
    const showThickness = [IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.LINE, IndicatorOutput_OutputType.COLUMN].includes(indicatorOutput_OutputTypeFromJSON(seriesOutput.type))

    const showPlotTypes = ![IndicatorOutput_OutputType.DRAWING, IndicatorOutput_OutputType.FILL, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN, IndicatorOutput_OutputType.OUTPUT_TYPE_UNKNOWN].includes(indicatorOutput_OutputTypeFromJSON(seriesOutput.type)) && seriesOutput.title !== 'Open Interest Outlook'

    const getIndicatorOutputTypeFromChartType = (chartType: ChartType, so: SeriesOutput) => {
        switch (chartType) {
            case ChartType.LINE:
                return so.type && [IndicatorOutput_OutputType.HZ_LINE, IndicatorOutput_OutputType.LINE].includes(indicatorOutput_OutputTypeFromJSON(so.type)) ? so.type : IndicatorOutput_OutputType.LINE
            case ChartType.AREA:
                return IndicatorOutput_OutputType.AREA
            case ChartType.HISTOGRAM:
                return IndicatorOutput_OutputType.HISTOGRAM
            case ChartType.COLUMN:
                return IndicatorOutput_OutputType.COLUMN
            case ChartType.FILL:
                return IndicatorOutput_OutputType.FILL
            default:
                return so.type
        }
    }

    const outputType = indicatorOutput_OutputTypeFromJSON(seriesOutput.type)
    const isOiProfileDrawing = outputType === IndicatorOutput_OutputType.DRAWING && seriesOutput.drawingType === 'oiProfile'

    const syncDrawingSettingsColor = (colorCode: string, colorName: string) => {
        if (!seriesOutput.drawingType) return seriesOutput.drawingSettings
        const colorKey = seriesOutput.drawingType === 'oiProfile'
            ? resolveOiProfileColorKey(colorName)
            : colorName
        if (!colorKey) return seriesOutput.drawingSettings
        return {
            settings: {
                ...(seriesOutput.drawingSettings?.settings ?? {}),
                [colorKey]: colorCode,
            },
        }
    }

    const handleColorChange = (colorCode: string, colorName: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const ind = newColors.findIndex((cl) => cl.title === colorName)
        if (ind > -1) {
            newColors[ind] = {
                title: colorName,
                colorCode: colorCode
            }
        }
        onChange({
            ...seriesOutput,
            colors: newColors,
            drawingSettings: syncDrawingSettingsColor(colorCode, colorName),
        })
    }

    const handleOiProfileColorChange = (key: string, label: string, colorCode: string) => {
        const newColors = [...(seriesOutput.colors ?? [])]
        const ind = newColors.findIndex((c) => c.title === key || c.title === label)
        if (ind > -1) {
            newColors[ind] = { title: newColors[ind].title, colorCode }
        } else {
            newColors.push({ title: label, colorCode })
        }
        onChange({
            ...seriesOutput,
            colors: newColors,
            drawingSettings: {
                settings: {
                    ...(seriesOutput.drawingSettings?.settings ?? {}),
                    [key]: colorCode,
                },
            },
        })
    }

    const handleThicknessChange = (thickness: LineThickness) => {
        onChange({
            ...seriesOutput,
            thickness,
        })
    }

    const lineThicknessProps = showThickness ? {
        lineThickness: clampLineThickness(seriesOutput.thickness),
        onLineThicknessChange: handleThicknessChange,
    } : {}

    const handlePlotTypeChange = (plotType: PlotTypesMenuItemType) => {
        const newOutput = {
            ...seriesOutput,
            style: plotType.style,
            subType: plotType.subType,
            type: getIndicatorOutputTypeFromChartType(plotType.type, seriesOutput)
        }
        onChange(newOutput)
    }



    if (Object.keys(seriesOutput).length < 1) {
        return null
    }

    if (isOiProfileDrawing) {
        return (
            <Grid container alignItems="center" spacing={1} sx={{ my: 0.5 }}>
                <Grid item xs={12}>
                    {!hideTitle && <Typography variant="subtitle1" sx={{ my: 1 }}>{seriesOutput.title}</Typography>}
                    {OI_PROFILE_COLOR_SETTINGS.map(({ key, label }) => {
                        const colorCode = getOiProfileColorValue(seriesOutput, key, label)
                        const tColor = tinycolor(colorCode)
                        return (
                            <Grid container key={key} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                <Grid item xs={4}><Typography color="inherit">{label}</Typography></Grid>
                                <Grid item xs={8}>
                                    <ColorSelector
                                        compact
                                        showOnlySwatch
                                        color={colorCode}
                                        opacity={tColor.getAlpha()}
                                        onColorChange={(clr, transparency) => {
                                            const newColor = tinycolor(clr).setAlpha(transparency).toHex8String()
                                            handleOiProfileColorChange(key, label, newColor)
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        )
                    })}
                </Grid>
            </Grid>
        )
    }

    return (
        <Grid container alignItems="center" spacing={1} sx={{ my: 0.5 }}>
            <Grid item xs={12}>

                {
                    (seriesOutput.colors?.length && seriesOutput.colors?.length > 1) ? (
                        <>
                            {!hideTitle && <Typography variant="subtitle1" sx={{ my: 1 }}>{seriesOutput.title}</Typography>}
                            {
                                seriesOutput.colors?.map((color, colorIndex) => {
                                    const tColor = tinycolor(color.colorCode);
                                    return (
                                        <Grid container key={color?.title ?? ''} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                            <Grid item xs={4}><Typography color="inherit">{color.title}</Typography></Grid>
                                            <Grid item xs={8}>
                                            <ColorSelector compact showOnlySwatch color={color.colorCode} opacity={tColor.getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String();
                                                    handleColorChange(newColor, color.title);
                                                }}
                                                {...(colorIndex === 0 ? lineThicknessProps : {})}
                                            />
                                            </Grid>
                                        </Grid>)
                                })
                            }
                            {
                                showPlotTypes && <Grid container key={'Plot type'} sx={{ px: hideTitle ? 0 : 2, my: 0.5 }}>
                                    <Grid item xs={4}><Typography color="inherit"
                                        sx={{ flexBasis: 2 }}>{'Plot type'}</Typography></Grid>
                                    <Grid item xs={8}>
                                        <SeriesOutputPlotTypesView
                                            outputChartType={getChartTypeFromOutput(seriesOutput.type || IndicatorOutput_OutputType.LINE)}
                                            outputStyle={seriesOutput.style || IndicatorOutput_OutputStyle.SOLID}
                                            outputSubType={seriesOutput.subType || ChartSubType.SIMPLE}
                                            onSelected={handlePlotTypeChange} />
                                    </Grid>
                                </Grid>
                            }
                        </>) : (
                        <Grid container>
                            <Grid item xs={6}>
                                <Grid container alignItems="center">
                                    <Checkbox
                                        disableRipple
                                        checked={seriesOutput.visibility?? true}
                                        onChange={(event, checked) => handleVisibilityChange(checked)}
                                    />
                                    <Typography
                                        color="inherit"
                                        variant="body2"
                                        fontWeight={400}
                                    >
                                        {seriesOutput.title}
                                    </Typography>
                                </Grid>
                            </Grid>
                            <Grid item xs={6} flexDirection={'row'} display={'flex'} gap={1} alignItems={'center'}>
                                <>
                                    {seriesOutput.colors?.length && <ColorSelector compact showOnlySwatch color={seriesOutput.colors[0].colorCode} opacity={tinycolor(seriesOutput.colors[0].colorCode).getAlpha()}
                                                onColorChange={(clr, transparency) => {
                                                    const newColor = tinycolor(clr).setAlpha(transparency).toHex8String();
                                                    handleColorChange(newColor, seriesOutput.colors?.[0].title ?? '');
                                                }}
                                                {...lineThicknessProps}
                                            />}
                                    {showPlotTypes && <SeriesOutputPlotTypesView
                                        outputChartType={getChartTypeFromOutput(seriesOutput.type || IndicatorOutput_OutputType.LINE)}
                                        outputStyle={seriesOutput.style || IndicatorOutput_OutputStyle.SOLID}
                                        outputSubType={seriesOutput.subType || ChartSubType.SIMPLE}
                                        onSelected={handlePlotTypeChange} />}
                                </>
                            </Grid>
                        </Grid>
                    )
                }
            </Grid>
        </Grid>
    )
}

export type SeriesGroupOutputProps = {
    seriesOutputsConfig: Record<string, SeriesOutput>,
    onSeriesOutputChange: (key: string, seriesOutput: SeriesOutput) => void
}

const SeriesGroupOutputs = ({ seriesOutputsConfig, onSeriesOutputChange }: SeriesGroupOutputProps) => {

    const keys = useMemo(() => Array.from(new Set(Object.keys(seriesOutputsConfig))).sort(), [seriesOutputsConfig])

    return (
        <Grid container gap={2}>
            {
                keys.map((key, index, keys) => (
                    <SeriesOutputView key={key} onChange={(seriesOutput: SeriesOutput) => {
                        onSeriesOutputChange(key, seriesOutput)
                    }} seriesOutput={seriesOutputsConfig[key]} hideTitle={keys.length <= 1} />
                ))
            }
        </Grid>
    )
}

export default memo(SeriesGroupOutputs)

